/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';

import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';

export interface RulesetModel extends SignalAdminResourceModel {
  [key: string]: any;
}

export interface RulesetLookupModel extends SignalAdminResourceModel {
  [key: string]: any;
}

@Injectable({providedIn: 'root'})
export class RulesetApiService extends SignalAdminResourceApiBase<RulesetModel> {
  constructor(client: AdnApiClientService) {
    super(client, 'rulesets');
  }

  listTeams(): Observable<SignalAdminResourcePage<RulesetLookupModel>> {
    return this.listLookup('teams', {minimal: true});
  }

  listLayouts(): Observable<SignalAdminResourcePage<RulesetLookupModel>> {
    return this.listLookup('layouts');
  }

  searchAdvertisers(query: string): Observable<RulesetLookupModel[]> {
    const normalizedQuery = (query || '').trim();
    const searchQuery = normalizedQuery && normalizedQuery.length < 3 && !normalizedQuery.endsWith('*')
      ? normalizedQuery + '*'
      : normalizedQuery;
    return this.client.request<{searchResults?: RulesetLookupModel[]}>({
      method: 'GET',
      url: this.resourceUri('search'),
      params: {
        q: searchQuery,
        types: 'Advertiser',
        objectState: 'ACTIVE',
        ...(!searchQuery ? {orderBy: 'NAME'} : {})
      }
    }).pipe(map((response) => response.searchResults || []));
  }

  searchAdvertiserTeams(query: string): Observable<RulesetLookupModel[]> {
    return this.listLookup('teams', {
      minimal: true,
      filterBy: 'name;description',
      filterByLike: query,
      where: 'type = AX_ADVERTISER'
    }).pipe(map((page) => page.results || []));
  }

  private listLookup(
    path: string,
    additionalParams: Record<string, any> = {}
  ): Observable<SignalAdminResourcePage<RulesetLookupModel>> {
    return this.client.request<SignalAdminResourcePage<RulesetLookupModel>>({
      method: 'GET',
      url: this.resourceUri(path),
      params: {
        includeHidden: false,
        includeActive: true,
        includeInactive: false,
        orderBy: 'name',
        pageSize: 1001,
        ...additionalParams
      }
    });
  }
}
